import { transformDateToLocalDateString } from "helpers";
import { FleetVehicleDamageRow } from "services/api/fleet/vehicles/damages/types";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import Tooltip from "ui/components/Tooltip/Tooltip";
import DamagePhotoGallery from "../DamagePhotoGallery/DamagePhotoGallery";

const STATUS_COLORS: Record<string, string> = {
  orange: "bg-orange-100 text-orange-800",
  blue: "bg-blue-100 text-blue-800",
  green: "bg-green-100 text-green-800",
  slate: "bg-slate-100 text-slate-800",
};

const STATUS_DOT_COLORS: Record<string, string> = {
  orange: "bg-orange-500",
  blue: "bg-blue-500",
  green: "bg-green-500",
  slate: "bg-slate-500",
};

/** Hrvatska množina: 1 oznaka, 2-4 oznake, 5+ oznaka. */
const pinCountLabel = (count: number): string => {
  const lastTwo = count % 100;
  const last = count % 10;

  if (lastTwo >= 11 && lastTwo <= 14) return "oznaka";
  if (last === 1) return "oznaka";
  if (last >= 2 && last <= 4) return "oznake";

  return "oznaka";
};

/**
 * Stupci razlaganja retka — oznake štete. Ovdje se definiraju samo zaglavlja;
 * ćelije crta `DamagePinRowChild`.
 */
export const FLEET_VEHICLE_DAMAGE_PIN_CHILD_COLUMNS = (): Column[] => [
  {
    title: "",
    key: "damage_pin_photo",
    field: "",
    type: ColumnTypes.string,
  },
  {
    title: "admin.fleet.vehicle.damage.pin.name",
    key: "damage_pin_name",
    field: "name",
    type: ColumnTypes.string,
  },
  {
    title: "admin.fleet.vehicle.damage.pin.description",
    key: "damage_pin_description",
    field: "description",
    type: ColumnTypes.string,
  },
  {
    title: "admin.fleet.vehicle.damage.pin.status",
    key: "damage_pin_status",
    field: "",
    type: ColumnTypes.string,
  },
];

export const FLEET_VEHICLE_DAMAGE_SEARCH_COLUMNS = (
  t: (key: string) => string,
  onDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void
): Column[] => [
  {
    // Sličica ide u vlastiti stupac bez naslova, kao i drugdje u tablicama.
    title: "",
    key: "damage_photo",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDamageRow) => {
      // Prva fotografija štete; bez nje ostaje ikona štete.
      const photos = row.photoUploads ?? [];
      const photoUrl = photos[0]?.url;

      const icon = (
        <TableIcon
          imageUrl={photoUrl}
          icon="/media/icons/damage.svg"
          size="medium"
          imageFit="cover"
        />
      );

      if (photos.length === 0) return icon;

      // Tooltip je interaktivan da se galerija može listati strelicama.
      return (
        <Tooltip position="bottom-right" trigger={icon} interactive>
          <DamagePhotoGallery photos={photos} />
        </Tooltip>
      );
    },
  },
  {
    title: "admin.fleet.vehicle.damage.search.name",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDamageRow) => {
      return (
        <div className="min-w-0">
          <div className="font-medium text-slate-900">{row.name}</div>
          {row.description && (
            <div className="text-sm text-slate-500 max-w-[300px] overflow-hidden text-ellipsis line-clamp-2">
              {row.description}
            </div>
          )}
        </div>
      );
    },
  },
  {
    // Vozilo u vlastitom stupcu — ista kartica kao u ostalim popisima, s detaljima na hover.
    title: "admin.fleet.vehicle.damage.search.vehicle",
    key: "damage_vehicle",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDamageRow) => (
      <VehicleCell vehicle={row.vehicle} />
    ),
  },
  {
    title: "admin.fleet.vehicle.damage.search.status",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDamageRow) => {
      const statusColor = row.vehicleDamageStatus?.color ?? "slate";
      return (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
            STATUS_COLORS[statusColor] ?? STATUS_COLORS.slate
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full ${
              STATUS_DOT_COLORS[statusColor] ?? STATUS_DOT_COLORS.slate
            }`}
          />
          {row.vehicleDamageStatus?.name}
        </span>
      );
    },
  },
  {
    title: "admin.fleet.vehicle.damage.search.damagedate",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDamageRow) => {
      return <div>{transformDateToLocalDateString(row.damageDate)}</div>;
    },
  },
  {
    // Broj označenih točaka na fotografijama. Prije je stupac čitao `pinImageId`,
    // polje starijeg modela s jednom točkom, koje unos štete više ne popunjava.
    title: "admin.fleet.vehicle.damage.search.pins",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDamageRow) => {
      const count = row.pins?.length ?? 0;

      if (count === 0) {
        return <span className="text-xs text-slate-400">-</span>;
      }

      return (
        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          {count} {pinCountLabel(count)}
        </span>
      );
    },
  },
  {
    title: "",
    field: "type",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleDamageRow) => {
      return (
        <ButtonMenu endIcon="/media/icons/more-dots.svg" variant="text">
          <ButtonMenuItem
            startIcon="/media/icons/trash.svg"
            variant="remove"
            onClick={(e) => onDeleteClick(e, row.id)}
          >
            {t("admin.delete")}
          </ButtonMenuItem>
        </ButtonMenu>
      );
    },
  },
];
